import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson'

/**
 * Everything upstream-shaped about SPC's convective outlooks and mesoscale
 * discussions. `geoJsonLayer.ts` is handed a plain `FeatureCollection` whose
 * properties are *ours*, so the case difference between the two publishing
 * routes, the `_ISO` fields that are sometimes absent, and the validity rule
 * never reach a MapLibre paint expression.
 *
 * URLs and shapes probed 2026-09-17 and re-probed at implementation time; see
 * docs/phase2.5-outlooks-plan.md section 3.
 *
 * The reason this file exists at all, rather than the layer fetching straight
 * into a `geojson` source: SPC publishes these products twice, and *both*
 * routes serve products that are no longer valid. The static files under
 * spc.noaa.gov are the worse offender — `day2otlk_prob` was serving a January
 * 2020 outlook under its current filename, with real polygons and current
 * colors — but the ArcGIS route has its own placeholder, a ~2 km all-null
 * triangle southeast of Nantucket for a Day 4-8 product with no area. Nothing
 * in either payload flags it except the expiry timestamp. So every feature is
 * checked against the clock before it draws, and `now` is a parameter rather
 * than a `Date.now()` read in here, so the tests assert the rule instead of
 * the clock.
 */

const VECTOR = 'https://mapservices.weather.noaa.gov/vector/rest/services'
export const OUTLOOKS = `${VECTOR}/outlooks/SPC_wx_outlks/MapServer`
export const MD_SERVICE = `${VECTOR}/outlooks/spc_mesoscale_discussion/MapServer`

export const SPC_ATTRIB =
  '<a href="https://www.spc.noaa.gov/">NOAA/NWS Storm Prediction Center</a>'

/** The outline MDs are drawn with: the app's `--warn`, which MapLibre paint
 *  cannot read as a CSS custom property. Outline only and no fill, because the
 *  MD service publishes no style of its own and an outline makes no claim
 *  about magnitude. */
export const MD_STROKE = '#f1c27d'

/**
 * A layer's `query` endpoint. `where` is evaluated server-side; `dn>0` drops
 * the Day 4-8 placeholder for 42 bytes, and the client-side rule below drops
 * it again so the guard survives a fallback to the static route.
 */
export function queryUrl(
  service: string,
  layer: number,
  where = '1=1',
  extra: Record<string, string> = {},
): string {
  const p = new URLSearchParams({
    where,
    outFields: '*',
    f: 'geojson',
    outSR: '4326',
    returnGeometry: 'true',
    ...extra,
  })
  return `${service}/${layer}/query?${p}`
}

/**
 * `202609181200` -> ms. UTC always: SPC stamps every product in Z.
 *
 * `new Date('202609181200')` is `Invalid Date` — it is not a format the Date
 * parser recognises — and `new Date(202609181200)` is a Tuesday in 1976.
 * Neither failure is loud, so build it from the digits.
 */
export function parseStamp(raw: unknown): number | null {
  if (typeof raw !== 'string' || !/^\d{12}$/.test(raw)) return null
  return Date.UTC(
    +raw.slice(0, 4),
    +raw.slice(4, 6) - 1,
    +raw.slice(6, 8),
    +raw.slice(8, 10),
    +raw.slice(10, 12),
  )
}

/**
 * What `geoJsonLayer.ts` paints with and `probe.ts` reads.
 *
 * A type alias rather than an interface, deliberately: only an alias of an
 * object literal type gets TypeScript's implicit index signature, which is
 * what makes it assignable to MapLibre's `GeoJsonProperties`.
 */
export type OutlookProps = {
  /** SPC's severity rank, and its intended draw order. */
  rank: number
  /** What SPC prints inside the area: `MRGL`, `SLGT`, `0.05`. */
  short: string
  /** What the menu says: "Marginal Risk", "5% Hail Risk". */
  full: string
  fill: string
  stroke: string
  /** ms since epoch. */
  from: number
  to: number
}

export type MdProps = {
  /** Four digits, zero-padded, as the `.txt` filename spells it. */
  number: string
  /** Drawn in the polygon and shown in the menu: "MD 2304". */
  short: string
  /** The service's own display string: "MD 2304 Active Till 2215 UTC". */
  full: string
  stroke: string
  rank: number
}

export type OutlookFeature = Feature<Polygon | MultiPolygon, OutlookProps>
export type MdFeature = Feature<Polygon | MultiPolygon, MdProps>

const HEX = /^#[0-9a-f]{6}$/i

/** A fresh empty collection: what a layer draws when its fetch failed. */
export function emptyCollection<P>(): FeatureCollection<Polygon | MultiPolygon, P> {
  return { type: 'FeatureCollection', features: [] }
}

function features(json: unknown): unknown[] {
  const fs = (json as { features?: unknown })?.features
  return Array.isArray(fs) ? fs : []
}

function props(f: unknown): Record<string, unknown> {
  const p = (f as { properties?: unknown })?.properties
  return p && typeof p === 'object' ? (p as Record<string, unknown>) : {}
}

/**
 * A polygon with at least one ring, or null. Drops the static files' "no area"
 * geometry, which is an empty `GeometryCollection` — valid GeoJSON that
 * geojson-vt renders as nothing, and therefore harmless right up until
 * something counts features or queries them.
 */
function polygon(f: unknown): Polygon | MultiPolygon | null {
  const g = (f as { geometry?: unknown })?.geometry as Polygon | MultiPolygon | undefined
  if (!g || !Array.isArray(g.coordinates) || g.coordinates.length === 0) return null
  if (g.type === 'Polygon') return g.coordinates[0]?.length ? g : null
  if (g.type === 'MultiPolygon') return g.coordinates[0]?.[0]?.length ? g : null
  return null
}

/** Case differs by route — `DN`/`LABEL` static, `dn`/`label` ArcGIS — while
 *  `fill` and `stroke` are lowercase in both. Normalize here so nothing
 *  downstream has to know which route a feature came from. */
function field(p: Record<string, unknown>, name: string): unknown {
  return p[name] ?? p[name.toUpperCase()]
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function color(value: unknown): string | null {
  const s = text(value)
  return HEX.test(s) ? s : null
}

/**
 * SPC's outlook features, normalized and checked against the clock.
 *
 * A feature draws only if all four hold:
 *
 * 1. its geometry is a polygon with at least one ring;
 * 2. `dn` parses and is greater than zero — the Day 4-8 "no area" placeholder
 *    is `dn: 0` with a real, tiny, tappable triangle;
 * 3. `fill` and `stroke` are both `#rrggbb` — `fill: null` and `fill: ""`
 *    arrive on real features from both routes and are MapLibre paint *errors*,
 *    not quiet nothings. A missing color is never coalesced to a default:
 *    inventing one would paint a product that has no area;
 * 4. `expire` parses and is still in the future.
 *
 * Rule 4 is the one this phase exists for. The compact `EXPIRE`/`expire` field
 * is what it reads, not `EXPIRE_ISO`, because the stale files predate the ISO
 * fields and carry neither.
 */
export function parseOutlooks(
  json: unknown,
  now: number,
): FeatureCollection<Polygon | MultiPolygon, OutlookProps> {
  const out: OutlookFeature[] = []
  for (const f of features(json)) {
    const geometry = polygon(f)
    if (!geometry) continue

    const p = props(f)
    const rank = Number(field(p, 'dn'))
    if (!Number.isFinite(rank) || rank <= 0) continue

    const fill = color(p.fill)
    const stroke = color(p.stroke)
    if (!fill || !stroke) continue

    const to = parseStamp(field(p, 'expire'))
    if (to === null || to <= now) continue

    const short = text(field(p, 'label'))
    if (!short) continue
    // `LABEL2` is empty on some real features, so the short code carries the
    // row on its own rather than the menu printing nothing.
    const full = text(field(p, 'label2')) || short

    out.push({
      type: 'Feature',
      geometry,
      properties: { rank, short, full, fill, stroke, from: parseStamp(field(p, 'valid')) ?? to, to },
    })
  }
  return { type: 'FeatureCollection', features: out }
}

/**
 * "MD 2304" -> "2304". Zero-padded to four digits: `md1.txt` is a 404 and
 * `md0001.txt` is a real product.
 */
export function mdNumber(name: unknown): string | null {
  const m = typeof name === 'string' ? /(\d{1,4})\s*$/.exec(name.trim()) : null
  return m ? m[1].padStart(4, '0') : null
}

/**
 * The text product's URL, built here and never taken from the feature's own
 * `popupinfo` — which is a third party naming the address we fetch, the exact
 * shape `app/api/alerts.py` refuses for CAP ids. (It is also `http://`, so
 * following it would be a mixed-content block as well as a bad idea.)
 */
export function mdTextUrl(number: string): string {
  if (!/^\d{4}$/.test(number)) throw new Error(`bad MD number: ${number}`)
  return `https://www.spc.noaa.gov/products/md/md${number}.txt`
}

/**
 * The MD service's active discussions.
 *
 * It carries **no expiration field** — the end of the window exists only
 * inside `folderpath` as display text with no date on it — so the service's
 * own filtering is the membership guard here, `folderpath` is shown verbatim,
 * and the authoritative window comes out of the text product once it is
 * fetched. Unlike `parseOutlooks` it takes no `now`: there is nothing in the
 * payload to compare a clock against.
 */
export function parseMds(json: unknown): FeatureCollection<Polygon | MultiPolygon, MdProps> {
  const out: MdFeature[] = []
  for (const f of features(json)) {
    const geometry = polygon(f)
    if (!geometry) continue
    const p = props(f)
    const number = mdNumber(p.name)
    if (!number) continue
    out.push({
      type: 'Feature',
      geometry,
      properties: {
        number,
        short: `MD ${Number(number)}`,
        full: text(p.folderpath) || `MD ${Number(number)}`,
        stroke: MD_STROKE,
        rank: 1,
      },
    })
  }
  return { type: 'FeatureCollection', features: out }
}

/**
 * The `Valid 171946Z - 172215Z` line out of an MD's text, which is the only
 * authoritative window these products publish — the map service has no
 * expiration field at all.
 *
 * `DDHHMM` names a day of some month, so it is resolved against `now`: the
 * candidate month, then its neighbours, nearest wins. An MD issued at 23:50Z
 * on the last of the month and read three minutes later must not land four
 * weeks out.
 */
export function parseMdValid(textBody: string, now: number): { from: Date; to: Date } | null {
  const m = /^\s*Valid\s+(\d{6})Z\s*-\s*(\d{6})Z\s*$/m.exec(textBody)
  if (!m) return null
  const from = resolveDayStamp(m[1], now)
  if (from === null) return null
  const to = resolveDayStamp(m[2], from)
  if (to === null) return null
  return { from: new Date(from), to: new Date(to) }
}

/** `DDHHMM` -> ms, in the month nearest `near`. */
function resolveDayStamp(raw: string, near: number): number | null {
  const day = +raw.slice(0, 2)
  const hour = +raw.slice(2, 4)
  const minute = +raw.slice(4, 6)
  if (day < 1 || day > 31 || hour > 23 || minute > 59) return null
  const ref = new Date(near)
  let best: number | null = null
  for (const shift of [-1, 0, 1]) {
    const t = Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth() + shift, day, hour, minute)
    // A shifted month that has no such day rolls over into the next one; that
    // candidate is simply further from `near` and loses.
    if (best === null || Math.abs(t - near) < Math.abs(best - near)) best = t
  }
  return best
}

/** An MD's text, read into the parts the discussion popup lays out. */
export interface MdText {
  /** "Parts of southern and central Idaho", one entry per `...` clause. */
  areas: string[]
  /** "Severe potential": what the discussion is about, without the watch clause. */
  concerning: string | null
  /** "Unlikely · 20%": the watch clause and the issuance probability, as given. */
  watch: string | null
  summary: string | null
  discussion: string[]
  /** "Most probable peak wind gust: 55-70 mph", when the product states them. */
  probable: string[]
  /** The ATTN and LAT...LON lines, as the wire has them. */
  attn: string[]
}

/**
 * Read a mesoscale discussion's wire text into its parts (Stage 10.5, 2d).
 *
 * The product is a sequence of blank-line-separated blocks, each opening with
 * its own label ("Areas affected...", "SUMMARY...", "ATTN..."), and the
 * forecaster's discussion runs on over further plain paragraphs until the
 * author line. Null when there is no SUMMARY and no DISCUSSION, which means
 * this is not the product this reader knows: the popup then shows the text
 * as it came, fixed-width, rather than a layout with nothing in it.
 *
 * Every string comes out as text. The popup interpolates it; nothing here
 * builds markup.
 */
export function parseMdText(body: string): MdText | null {
  const blocks = body
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean)
  const flat = (s: string) => s.replace(/\s*\n\s*/g, ' ').trim()
  const clauses = (s: string) =>
    flat(s)
      .split('...')
      .map((c) => c.trim())
      .filter(Boolean)

  const out: MdText = {
    areas: [],
    concerning: null,
    watch: null,
    summary: null,
    discussion: [],
    probable: [],
    attn: [],
  }
  let watchWord: string | null = null
  let percent: string | null = null
  let inDiscussion = false

  for (const block of blocks) {
    let m: RegExpExecArray | null
    if ((m = /^Areas affected\.\.\.([\s\S]*)$/i.exec(block))) {
      out.areas = clauses(m[1])
    } else if ((m = /^Concerning\.\.\.([\s\S]*)$/i.exec(block))) {
      const parts = clauses(m[1])
      const watch = parts.find((p) => /^watch\s+(unlikely|likely|possible)$/i.test(p))
      if (watch) watchWord = capitalise(watch.replace(/^watch\s+/i, ''))
      const rest = parts.filter((p) => p !== watch)
      out.concerning = rest.length ? rest.join(' · ') : null
    } else if ((m = /^Probability of Watch Issuance\.\.\.\s*(\d+)\s*percent/i.exec(block))) {
      percent = `${m[1]}%`
    } else if ((m = /^SUMMARY\.\.\.([\s\S]*)$/.exec(block))) {
      out.summary = flat(m[1])
      inDiscussion = false
    } else if ((m = /^DISCUSSION\.\.\.([\s\S]*)$/.exec(block))) {
      out.discussion.push(flat(m[1]))
      inDiscussion = true
    } else if (/^ATTN\.\.\./.test(block) || /^LAT\.\.\.LON/.test(block)) {
      out.attn.push(flat(block).replace(/\s{2,}/g, ' '))
      inDiscussion = false
    } else if (/^MOST PROBABLE/i.test(block)) {
      for (const line of block.split('\n')) {
        const p = /^MOST PROBABLE\s+(.+?)\.\.\.(.+)$/i.exec(line.trim())
        if (p) out.probable.push(`Most probable ${p[1].toLowerCase()}: ${p[2].trim().toLowerCase()}`)
      }
      inDiscussion = false
    } else if (/^\.\.[^.]/.test(block) || /^\.\.\.Please see/i.test(block) || /^NNNN$/.test(block)) {
      // The author line and the boilerplate after it end the discussion.
      inDiscussion = false
    } else if (inDiscussion) {
      out.discussion.push(flat(block))
    }
  }

  out.watch = [watchWord, percent].filter(Boolean).join(' · ') || null
  if (!out.summary && out.discussion.length === 0) return null
  return out
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}
